/**
 * Portfolio value line on a frosted panel (Phase 8, D53). One series → no legend; the panel
 * title names it. A blue line with a soft fill, a crosshair + tooltip on hover/touch, and
 * arrow-key stepping when focused. Values shown are the API's decimal strings — the numbers are
 * only turned into floats to place points on screen, never for money maths.
 */
import type { FiatCurrency, PortfolioHistory } from "@custos/api-client";
import { formatDateTime, formatFiat } from "@custos/ui";
import { useId, useMemo, useState, type KeyboardEvent, type PointerEvent } from "react";

const W = 1000;
const H = 300;
const PAD_TOP = 16;
const PAD_BOTTOM = 12;

interface Pt {
  i: number;
  t: string;
  value: string;
  x: number;
  y: number;
}

export function PortfolioChart({
  history,
  currency,
  className,
}: {
  history: PortfolioHistory | undefined;
  currency: FiatCurrency;
  className?: string;
}) {
  const gradientId = useId();
  const [active, setActive] = useState<number | null>(null);

  const { pts, path, area } = useMemo(() => {
    const raw = history?.points ?? [];
    const known = raw.map((p, i) => ({ ...p, i })).filter((p) => p.value !== null) as {
      t: string;
      value: string;
      i: number;
    }[];
    if (known.length < 2) return { pts: [] as Pt[], path: "", area: "" };
    const nums = known.map((p) => Number(p.value));
    const min = Math.min(...nums);
    const max = Math.max(...nums);
    const span = max - min || Math.max(max, 1) * 0.1 || 1;
    const lo = min - span * 0.1;
    const hi = max + span * 0.1;
    const last = raw.length - 1;
    const pts: Pt[] = known.map((p, k) => ({
      ...p,
      x: (p.i / last) * W,
      y: PAD_TOP + (1 - (nums[k]! - lo) / (hi - lo)) * (H - PAD_TOP - PAD_BOTTOM),
    }));
    // Gaps (unknown values) break the line rather than being bridged with a guess.
    let d = "";
    pts.forEach((p, k) => {
      const prev = pts[k - 1];
      d += `${!prev || p.i - prev.i > 1 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`;
    });
    const a =
      `M${pts[0]!.x.toFixed(1)},${H}` +
      pts.map((p) => `L${p.x.toFixed(1)},${p.y.toFixed(1)}`).join("") +
      `L${pts.at(-1)!.x.toFixed(1)},${H}Z`;
    return { pts, path: d, area: a };
  }, [history]);

  if (!history) {
    return <div className={className} aria-busy="true" />;
  }
  if (pts.length === 0) {
    return (
      <div className={`${className ?? ""} flex items-center justify-center`}>
        <p className="rounded-full bg-surface/70 px-4 py-2 text-center text-sm text-muted">
          No price history for this range yet — the chart fills in as prices are recorded.
        </p>
      </div>
    );
  }

  const shown = active === null ? null : pts[Math.min(active, pts.length - 1)]!;
  const pick = (e: PointerEvent<HTMLDivElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - box.left) / box.width) * W;
    let best = 0;
    for (let k = 1; k < pts.length; k += 1)
      if (Math.abs(pts[k]!.x - x) < Math.abs(pts[best]!.x - x)) best = k;
    setActive(best);
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const cur = active ?? pts.length - 1;
    setActive(Math.max(0, Math.min(pts.length - 1, cur + (e.key === "ArrowRight" ? 1 : -1))));
  };
  const first = pts[0]!;
  const last = pts.at(-1)!;

  return (
    <div
      className={`${className ?? ""} relative touch-pan-y outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded-2xl`}
      role="group"
      tabIndex={0}
      aria-label={`Portfolio value chart, ${history.range}. From ${formatFiat(first.value, currency)} to ${formatFiat(last.value, currency)}. Use the arrow keys to read values.`}
      data-testid="portfolio-chart"
      onPointerMove={pick}
      onPointerDown={pick}
      onPointerLeave={() => setActive(null)}
      onBlur={() => setActive(null)}
      onKeyDown={onKey}
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="absolute inset-0 size-full"
        aria-hidden
      >
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="var(--primary)" stopOpacity="0.32" />
            <stop offset="1" stopColor="var(--primary)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#${gradientId})`} />
        <path
          d={path}
          fill="none"
          stroke="var(--primary)"
          strokeWidth={2.5}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          data-testid="portfolio-line"
        />
        {shown && (
          <line
            x1={shown.x}
            x2={shown.x}
            y1={0}
            y2={H}
            stroke="var(--text-muted)"
            strokeOpacity={0.5}
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>
      {shown && (
        <>
          <span
            aria-hidden
            className="pointer-events-none absolute size-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-surface bg-primary"
            style={{ left: `${(shown.x / W) * 100}%`, top: `${(shown.y / H) * 100}%` }}
          />
          <div
            role="status"
            className="pointer-events-none absolute top-0 rounded-2xl bg-fg px-3 py-2 text-xs text-bg"
            style={
              shown.x > W / 2
                ? { right: `${100 - (shown.x / W) * 100}%`, marginRight: 12 }
                : { left: `${(shown.x / W) * 100}%`, marginLeft: 12 }
            }
          >
            <p className="font-mono text-sm tabular-nums">{formatFiat(shown.value, currency)}</p>
            <p className="opacity-70">{formatDateTime(shown.t)}</p>
          </div>
        </>
      )}
    </div>
  );
}
